import { Nullable, StrictNullable } from '@/shared/types/app.types'
import { ColDef, ColGroupDef } from '@ag-grid-enterprise/all-modules'
import { GridOptions } from '@ag-grid-community/core/dist/cjs/entities/gridOptions'

export enum ViewValueMode {
    COMPACT = 'compact',
    NORMAL = 'normal'
}
export enum ViewIconMode {
    COMPACT = 'fal fa-align-justify',
    NORMAL = 'fal fa-bars'
}
export enum ViewSizeMode {
    COMPACT = 25,
    NORMAL = 40
}

export interface IModeView {
    value: ViewValueMode
    icon: ViewIconMode
    size: ViewSizeMode
}
export const defaultModeViewAgGrid: IModeView = {
    value: ViewValueMode.COMPACT,
    icon: ViewIconMode.COMPACT,
    size: ViewSizeMode.COMPACT
}

export interface ISort {
    sorted: boolean
    unsorted: boolean
    empty: boolean
}

export interface IPageable {
    sort: ISort
    pageNumber: number
    pageSize: number
    offset: number
    unpaged: boolean
    paged: boolean
}

export interface IResponseAgGrid<T> {
    content: T[]
    pageable: IPageable
    last: boolean
    totalElements: number
    totalPages: number
    first: boolean
    numberOfElements: number
    sort: ISort
    size: number
    number: number
    empty: boolean
}

///

export interface ScrollPage {
    scrollId: Nullable<Object>
    size: number
    sort: {
        sort: string
    },
    totalElements: number
}
export interface InfiniteScrollResponse<T>{
    content: T[]
    scrollPage: ScrollPage
}

///

export enum AgGridColumnType {
    AMENDMENT_RENDERER = 'amendmentRenderer',
    ASSOCIATED_CASES = 'associatedCases',
    ATTACHMENT_COUNT = 'attachmentCount',
    CENTERS = 'centers',
    CHECK_ICON = 'checkIconRenderer',
    COMMON_ASSAY_STATUS = 'commonAssayStatus',
    COMMON_CASE_STATUS = 'commonCaseStatus',
    COMMON_PORTION_STATUS = 'commonPortionStatus',
    COMMON_REQUEST_STATUS = 'commonRequestStatus',
    COMMON_SAMPLE_STATUS = 'commonSampleStatus',
    COMMON_SUBPORTION_STATUS = 'commonSubportionStatus',
    CUSTOM_CHECKBOX = 'customCheckbox',
    DATE = 'date',
    DATETIME = 'dateTime',
    DECIMAL = 'decimal',
    DEPARTMENTS = 'departments',
    DIACRITICS_INSENSITIVE_TEXT_FOR_CODE_DESCRIPTION_OR_STRING = 'diacriticsInsensitiveTextForCodeDescriptionOrString',
    FIXED_WIDTH = 'fixedWidth',
    FLAGS_RENDERER = 'flagsRenderer',
    GENDERS = 'genders',
    GROSSING_SAMPLE_STATUS = 'grossingSampleStatus',
    HAS_UNAUTHORIZED_BILLABLE_CONCEPTS = 'hasUnauthorizedBillableConcepts',
    HIS = 'his',
    INCIDENT = 'incident',
    INCIDENT_CORE_ENTITY = 'incidentCoreEntity',
    INCIDENT_ORIGIN = 'incidentOrigin',
    INCIDENT_SEVERITY = 'incidentSeverity',
    INCIDENT_TYPE = 'incidentType',
    LABORATORIES = 'laboratories',
    LABORATORY_SAMPLE_STATUS = 'laboratorySampleStatus',
    MASTER_DETAIL = 'masterDetail',
    NOTIFICATION_ENTITY_TYPE = 'notificationEntityType',
    NUMBER = 'number',
    PATIENT_AGE = 'patientAge',
    PRIORITY = 'priority',
    PRIORITY_COLOR_RENDERER = 'priorityColorRenderer',
    READ_INDICATOR = 'readIndicator',
    SAMPLE_VIRTUAL_SHELF = 'sampleVirtualShelf',
    SHIPMENT_CORE_ENTITY = 'shipmentCoreEntity',
    SHIPMENT_ENTITY_STATUS = 'shipmentEntityStatus',
    SHIPMENT_REQUEST_STATUS = 'shipmentRequestStatus',
    SUPPRESS_ALL = 'suppressAll',
    TEXT = 'text',
    WASTE_SAMPLE_STATUS = 'wasteSampleStatus',
    BILLING_COPAYMENT_TYPE = 'billingCopaymentType'
}

export enum AgGridExportType {
    BARCODE = 'barcode',
    BOOLEAN = 'boolean',
    DATE = 'date',
    DATETIME = 'dateTime',
    DOUBLE = 'double',
    FLOAT = 'float',
    INTEGER = 'integer',
    NUMBER = 'number',
    SET = 'set',
    TEXT = 'text'
}

export enum AgGridColumnFilter {
    DATE = 'agDateColumnFilter',
    NUMBER = 'agNumberColumnFilter',
    TEXT = 'agTextColumnFilter'
}

export interface ExportedColDef extends ColDef {
    exportType: AgGridExportType
    exportField?: string
    exportDisabled?: boolean
    useForGeneratingBarcodeInExports?: boolean
}
export interface ExportedColGroupDef extends ColGroupDef {
    children: (ColDef | ColGroupDef | ExportedColDef | ExportedColGroupDef)[]
}

export type AgColumnDefinition =
    ColDef |
    ColGroupDef |
    ExportedColDef |
    ExportedColGroupDef

///

export enum AgGridCallbackEventType {
    SUCCESS = 'successCallback',
    WARN = 'warnCallback',
    FAIL = 'failCallback',
    END = 'endCallback'
}

export interface AgGridCallbackEvent {
    type: AgGridCallbackEventType
    error?: any
}

export interface AgGridOptions extends GridOptions {
    columnDefs?: StrictNullable<AgColumnDefinition[]>
    columnTypes?: Record<AgGridColumnType, ColDef | ExportedColDef>
}

export enum AgGridRowModelType {
    CLIENT_SIDE = 'clientSide',
    SERVER_SIDE = 'serverSide'
}

export enum AgGridServerSideStoreType {
    PARTIAL = 'partial',
    FULL = 'full'
}
